import { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { assertCondition } from '../../../../utils/types';
import {
  IRetentionAverageReturnCell,
  IRetentionReturnCell,
} from '../../interfaces';
import { CHART_TYPES } from '../../constants/chartTypes';
import useTooltipScrollCancellation from './useTooltipScrollCancellation';
import { useChartContext } from '../../ChartContext';

export interface IRetentionTooltipProps {
  isChurn: boolean;
  key: string;
  returnCell: IRetentionReturnCell | IRetentionAverageReturnCell;
  sliceKey: string;
  returnIndex: number;
  chartType: CHART_TYPES;
}

const useRetentionTooltip = () => {
  const {
    metadata: { query },
  } = useChartContext();
  assertCondition(query.__typename === 'RetentionQuery');
  const [tooltipProps, setTooltipProps] =
    useState<IRetentionTooltipProps | null>(null);
  const [isTooltipLocked, setIsTooltipLocked] = useState(false);
  const debouncedSetTooltipProps = debounce(setTooltipProps, 100);
  const onMouseCellMove = useCallback(
    ({
      key,
      returnCell,
      sliceKey,
      isChurn,
      returnIndex,
      chartType,
    }: IRetentionTooltipProps) => {
      if (isTooltipLocked) {
        return;
      }

      // Clear any pending leave timeout since mouse moved
      debouncedSetTooltipProps.cancel();
      setTooltipProps({
        key,
        returnCell,
        sliceKey,
        isChurn,
        returnIndex,
        chartType,
      });
    },
    [isTooltipLocked, debouncedSetTooltipProps],
  );

  const onMouseCellLeave = useCallback(() => {
    if (isTooltipLocked) {
      return;
    }
    // Set tooltip to null after 50ms if no mouse movement
    debouncedSetTooltipProps(null);
  }, [isTooltipLocked, debouncedSetTooltipProps]);

  const onCellClick = useCallback(
    ({
      key,
      returnCell,
      sliceKey,
      isChurn,
      returnIndex,
      chartType,
    }: IRetentionTooltipProps) => {
      setIsTooltipLocked(true);
      setTooltipProps({
        key,
        returnCell,
        sliceKey,
        isChurn,
        returnIndex,
        chartType,
      });
    },
    [],
  );

  const clearLockedTooltipProps = useCallback(() => {
    if (isTooltipLocked) {
      setIsTooltipLocked(false);
      setTooltipProps(null);
    }
  }, [isTooltipLocked]);

  useTooltipScrollCancellation({
    closeTooltip: () => {
      setIsTooltipLocked(false);
      setTooltipProps(null);
    },
  });

  return {
    tooltipProps,
    isTooltipLocked,
    onMouseCellMove,
    onMouseCellLeave,
    onCellClick,
    clearLockedTooltipProps,
  };
};

export default useRetentionTooltip;
